import { effectScope, watch } from 'vue';
import { useForm } from '/src/composables/useForm.js';
import { useAuthStore } from '/src/store/stores/useAuthStore.js';
import { useNetworkStore } from '/src/store/stores/useNetworkStore.js';
import { useClockStore } from '/src/store/stores/useClockStore.js';
import { useDeviceStore } from '/src/store/stores/useDeviceStore.js';
import { useNativeApp } from '/src/native/useNativeApp.js';
import { useLogger } from '/src/composables/useLogger.js';
import { notify } from '/src/components/notifications';
import { capitalize } from '/src/utils/strings.js';
import { randomBetween } from '/src/utils/numbers.js';
import i18n from '/src/locale';

import {
	offline,
	uploadClockDocument,
} from '/src/apis/clocks.js';

const DRAIN_INTERVAL = 60 * 1000;
const RETRY_MAX_MINUTES = 15;
const MAX_ATTEMPTS = 50;
const MAX_ACCEPTED_DELAY = RETRY_MAX_MINUTES * 60 * 1000;
const STUCK_QUEUE_THRESHOLD = 30 * 60 * 1000;
const STUCK_TELEMETRY_THROTTLE = 60 * 60 * 1000;
const OFFLINE_REQUEST_TIMEOUT = 30 * 1000;

const QUEUE_METADATA_FIELDS = ['uid', 'status', 'attempts', 'nextRetryAt', 'lastError', 'failedAt'];

const { network } = useNetworkStore();

const {
	isReady: isAuthStoreReady,
} = useAuthStore();

const {
	pendingClocks,
	removeClock,
	updateClock,
	markClockFailed,
	normalizeClocks,
	isReady: isClockStoreReady,
} = useClockStore();

const {
	resolveDeviceId,
	isReady: isDeviceStoreReady,
} = useDeviceStore();

const { addListener } = useNativeApp();
const { addActionLog } = useLogger();

// serialize drains per employee (shared across service and components)
const employeeDrainLocks = new Map();
let isDraining = false;
let lastStuckTelemetryAt = 0;

// transient 4xx codes: request timeout, too early, rate limited
const RETRYABLE_4XX = [408, 425, 429];

function isNonRetryable (rawError) {
	const status = rawError?.response?.status;
	return status >= 400 && status < 500 && !RETRYABLE_4XX.includes(status);
}

function getRetryDelay (attempts) {
	const retryMinutes = Math.min(attempts, RETRY_MAX_MINUTES);

	return randomBetween(retryMinutes / 2, retryMinutes) * 60 * 1000;
}

function toApiPayload (clock) {
	return Object.keys(clock).reduce((payload, key) => {
		if (!QUEUE_METADATA_FIELDS.includes(key)) {
			payload[key] = clock[key];
		}
		return payload;
	}, {});
}

function notifyDeadLetter (clock, rawError) {
	const status = rawError?.response?.status;

	notify({
		title: capitalize(i18n.global.t('clockin_offline_discarded')),
		text: capitalize(i18n.global.t('clockin_offline_discarded_description')),
		type: 'error',
	});

	addActionLog('offline clock dead-lettered', {
		employeeId: clock.employeeId,
		uid: clock.uid,
		status,
	});
}

function reportStuckQueue () {
	const oldest = pendingClocks.value.reduce((oldest, clock) => {
		return !oldest || clock.date < oldest.date ? clock : oldest;
	}, null);

	if (!oldest || (Date.now() - oldest.date * 1000) < STUCK_QUEUE_THRESHOLD) {
		return;
	}

	if (Date.now() - lastStuckTelemetryAt < STUCK_TELEMETRY_THROTTLE) {
		return;
	}
	lastStuckTelemetryAt = Date.now();
}

function groupClocksByEmployee (clocks) {
	return clocks?.reduce((groups, clock) => {
		const employeeId = clock.employeeId;
		if (!groups[employeeId]) {
			groups[employeeId] = [];
		}
		groups[employeeId].push(clock);
		return groups;
	}, {}) || {};
}

async function processEmployeeClocks (employeeId, employeeClocks) {
	for (const clock of employeeClocks) {
		const now = Date.now();

		if (clock.nextRetryAt > now && clock.nextRetryAt <= now + MAX_ACCEPTED_DELAY) {
			// still backing off: stop here to preserve FIFO for this employee
			return;
		}

		// backstop: fill deviceId while draining (device store is hydrated here)
		const payload = toApiPayload({
			...clock,
			deviceId: resolveDeviceId(clock.deviceId),
		});

		const scope = effectScope();
		try {
			// never log the full clock: it carries code/pin/coordinates
			console.log('Processing clock: ', {
				uid: clock.uid,
				employeeId: clock.employeeId,
				action: clock.action,
				date: clock.date,
				attempts: clock.attempts,
			});
			const form = scope.run(() => useForm(payload));

			let failed = false;
			let rawError = null;

			await offline(form, {
				timeout: OFFLINE_REQUEST_TIMEOUT,
				async onSuccess ({ id }) {
					if (form.document) {
						uploadClockDocument(form, id);
					}

					removeClock(clock);
				},
				onError (errors, raw) {
					failed = true;
					rawError = raw;
				},
			}).catch(() => {});

			if (!failed) {
				continue;
			}

			if (isNonRetryable(rawError)) {
				// dead-letter: unblock the queue and keep processing this employee
				console.log(`Offline clock rejected (dead-letter) for employee ${employeeId}: `, rawError?.response?.status);
				markClockFailed(clock.uid, rawError?.response?.status ?? rawError);
				notifyDeadLetter(clock, rawError);
				continue;
			}

			// retryable (5xx / network): backoff and stop this employee's queue (FIFO)
			const attempts = (clock.attempts ?? 0) + 1;
			if (attempts >= MAX_ATTEMPTS) {
				markClockFailed(clock.uid, `max attempts (${attempts})`);
				notifyDeadLetter(clock, rawError);
				continue;
			}

			console.log(`Offline clock error for employee ${employeeId} (attempt ${attempts}): `, rawError?.message ?? rawError);
			updateClock(clock.uid, {
				attempts,
				nextRetryAt: Date.now() + getRetryDelay(attempts),
				lastError: String(rawError?.message ?? rawError ?? 'unknown'),
			});

			return;
		} finally {
			scope.stop();
		}
	}
}

async function drainEmployeeClocks (employeeId) {
	// normalize at the boundary: the lock map and the filters must share key type
	employeeId = String(employeeId);

	// re-check after every await: a concurrent caller may have re-locked
	while (employeeDrainLocks.has(employeeId)) {
		await employeeDrainLocks.get(employeeId);
	}

	const employeeClocks = pendingClocks.value
		.filter(clock => String(clock.employeeId) === employeeId);

	if (!employeeClocks.length) {
		return { remaining: 0 };
	}

	const promise = processEmployeeClocks(employeeId, employeeClocks)
		.finally(() => employeeDrainLocks.delete(employeeId));
	employeeDrainLocks.set(employeeId, promise);
	await promise;

	const remaining = pendingClocks.value
		.filter(clock => String(clock.employeeId) === employeeId).length;

	return { remaining };
}

async function processOfflineClocks () {
	if (isDraining) {
		return;
	}

	isDraining = true;
	try {
		const clocksByEmployee = groupClocksByEmployee(pendingClocks.value);

		const employeeProcessingPromises = Object.keys(clocksByEmployee).map(
			(employeeId) => drainEmployeeClocks(employeeId),
		);

		await Promise.allSettled(employeeProcessingPromises);

		reportStuckQueue();
	} finally {
		isDraining = false;
	}
}

export function useClockService () {
	let removeWatch;
	let drainInterval;
	let removeAppListener;

	async function start () {
		stop();

		await isAuthStoreReady();
		await isClockStoreReady();
		await isDeviceStoreReady();

		normalizeClocks();

		removeWatch = watch(() => network.value, (active) => {
			if (!active) {
				return;
			}

			processOfflineClocks();
		}, { immediate: true });

		drainInterval = setInterval(() => {
			if (!network.value) {
				return;
			}

			processOfflineClocks();
		}, DRAIN_INTERVAL);

		removeAppListener = addListener('appStateChange', ({ isActive }) => {
			if (isActive && network.value) {
				processOfflineClocks();
			}
		});
	}

	function stop () {
		if (removeWatch) {
			removeWatch();
			removeWatch = null;
		}

		if (drainInterval) {
			clearInterval(drainInterval);
			drainInterval = null;
		}

		if (removeAppListener) {
			removeAppListener();
			removeAppListener = null;
		}
	}

	return {
		start,
		stop,
		drainEmployeeClocks,
		processOfflineClocks,
	};
}
